/**
 * Runner, 3D-режим (GEN-18191): улица, персонаж и препятствия в three.js.
 * Механика игры — не здесь: приложение (калибровка, поза, спавн, столкновения,
 * жизни) каждый кадр отдаёт состояние через `setState`, сцена только рисует.
 * Один модуль на iOS, Android и (позже) ТВ — правки сцены только здесь.
 *
 * Улица — `runner_street.glb` из Android (aos-groove-app/tools/runner-environment,
 * соглашения — в его README): метры, +Y вверх, движение к −Z, тайл 60 м
 * повторяется, `Backdrop` едет за камерой. Персонаж — `runner.glb`:
 * манекен из Universal Animation Library (Quaternius, CC0) с клипами бега,
 * прыжка, переката и удара; время клипа ведёт прогресс действия из игры. Препятствия — примитивы.
 *
 * Карта `datacenter` — коридор ЦОД (`dataCenterWorld.ts`): мир и препятствия
 * строятся кодом, `runner_street.glb` не грузится.
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { createDataCenterWorld, DATA_CENTER_BACKGROUND, type DataCenterWorld } from './dataCenterWorld.ts';

export type Runner3DAction = 'WALK' | 'JUMP' | 'DUCK' | 'PUNCH' | 'DODGE';

export interface Runner3DObstacle {
  readonly id: number;
  readonly type: Exclude<Runner3DAction, 'WALK'>;
  /** 0 — левая, 1 — центр, 2 — правая. */
  readonly lane: number;
  readonly fullWidth?: boolean;
  /** Позиция движка 0…1000; игрок на 880. */
  readonly z: number;
  readonly destroyed?: boolean;
}

export interface Runner3DState {
  readonly running: boolean;
  readonly player: {
    readonly lane: number;
    readonly action: Runner3DAction;
    /** Прогресс действия 0…1. */
    readonly progress: number;
    readonly lives: number;
  };
  readonly obstacles: readonly Runner3DObstacle[];
}

export type Runner3DWorld = 'street' | 'datacenter';

export interface Runner3DOptions {
  readonly host: HTMLElement;
  /** Путь к моделям: `<assetsBase>runner_street.glb`, `<assetsBase>runner.glb`. */
  readonly assetsBase?: string;
  /** Карта; по умолчанию `street`. */
  readonly world?: Runner3DWorld;
  readonly pixelRatio?: number;
  readonly antialias?: boolean;
  readonly onReady?: () => void;
  readonly onError?: (message: string) => void;
}

export interface Runner3DScene {
  setState(state: Runner3DState): void;
  start(): void;
  pause(): void;
  resume(): void;
  restart(): void;
  destroy(): void;
}

// ─── Масштаб и раскладка ─────────────────────────────────────────────────
// Препятствие едет 200 ед/с движка, улица — 15 м/с: 1 ед = 0,075 м. Игрок
// стоит на 880 ед, препятствие появляется в ~66 м перед ним.
const UNIT = 0.075;
const PLAYER_UNITS = 880;
const STREET_SPEED = 200 * UNIT;
const TILE_LENGTH = 60;
const TILES_AHEAD = 5;
const LANE_X = [-5, 0, 5];
/** Рост персонажа, м: раза в два с лишним выше стенки (1,2 м) — как было на глаз со старой моделью. */
const PLAYER_HEIGHT = 2.8;
const CAMERA_BACK = 11;
const CAMERA_UP = 6.2;
const LOOK_AHEAD = 15;

/** Клипы персонажа (имена из Universal Animation Library). */
const CLIP = {
  run: 'Sprint_Loop',
  jumpStart: 'Jump_Start',
  jumpLoop: 'Jump_Loop',
  jumpLand: 'Jump_Land',
  roll: 'Roll',
  punch: 'Punch_Cross',
} as const;
/** Доля действия на вход и выход клипа из бега. */
const CLIP_BLEND = 0.08;

function laneX(lane: number): number {
  return LANE_X[Math.max(0, Math.min(2, Math.round(lane)))];
}

const smoothstep = (t: number): number => {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
};

export function createRunner3DScene(options: Runner3DOptions): Runner3DScene {
  const base = options.assetsBase ?? '';
  const fail = (message: string): void => options.onError?.(message);

  const renderer = new THREE.WebGLRenderer({ antialias: options.antialias ?? false, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, options.pixelRatio ?? 1.5));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  const canvas = renderer.domElement;
  canvas.style.cssText = 'display:block;position:absolute;inset:0;width:100%;height:100%;';
  options.host.appendChild(canvas);

  const scene = new THREE.Scene();
  const dataCenter: DataCenterWorld | null = options.world === 'datacenter' ? createDataCenterWorld() : null;
  if (dataCenter) {
    // Холодный свет серверной; туман прячет конец коридора.
    scene.background = new THREE.Color(DATA_CENTER_BACKGROUND);
    scene.fog = new THREE.Fog(DATA_CENTER_BACKGROUND, 70, 230);
    scene.add(new THREE.HemisphereLight(0xcfe0ff, 0x2a3348, 2.2));
    const top = new THREE.DirectionalLight(0xdbeafe, 1.4);
    top.position.set(10, 60, 40);
    scene.add(top);
    scene.add(dataCenter.decor);
  } else {
    scene.background = new THREE.Color(0xa8d0f0);
    scene.add(new THREE.HemisphereLight(0xd6e8ff, 0xc2b2a0, 2.4));
    const sun = new THREE.DirectionalLight(0xfff0d8, 1.7);
    sun.position.set(-60, 100, 70);
    scene.add(sun);
  }

  const camera = new THREE.PerspectiveCamera(55, 16 / 9, 0.3, 720);

  // ─── Материалы и геометрия препятствий ─────────────────────────────────
  const lambert = (color: number): THREE.MeshLambertMaterial => new THREE.MeshLambertMaterial({ color, flatShading: true });
  const box = new THREE.BoxGeometry(1, 1, 1);
  const mats = {
    brick: lambert(0xb5523b),
    cap: lambert(0xe8e2d4),
    post: lambert(0x5b6068),
    yellow: lambert(0xf2c230),
    black: lambert(0x2a2a2e),
    wood: lambert(0xc08a4a),
    woodDark: lambert(0x7a5230),
    red: lambert(0xd8352a),
    orange: lambert(0xff7a1a),
    white: lambert(0xf4f4f4),
    shadow: new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.28, depthWrite: false }),
  };
  const shadowGeo = new THREE.CircleGeometry(1, 24);
  shadowGeo.rotateX(-Math.PI / 2);

  function part(parent: THREE.Object3D, geo: THREE.BufferGeometry, mat: THREE.Material,
    x: number, y: number, z: number, sx = 1, sy = 1, sz = 1): THREE.Mesh {
    const m = new THREE.Mesh(geo, mat);
    m.position.set(x, y, z);
    m.scale.set(sx, sy, sz);
    parent.add(m);
    return m;
  }

  function blobShadow(parent: THREE.Object3D, rx: number, rz: number): void {
    const s = part(parent, shadowGeo, mats.shadow, 0, 0.17, 0, rx, 1, rz);
    s.renderOrder = 1;
  }

  /** Низкая кирпичная стенка — перепрыгнуть. */
  function makeWall(width: number): THREE.Group {
    const g = new THREE.Group();
    part(g, box, mats.brick, 0, 0.55, 0, width, 1.1, 0.7);
    part(g, box, mats.cap, 0, 1.16, 0, width + 0.15, 0.14, 0.85);
    blobShadow(g, width * 0.55, 0.6);
    return g;
  }

  /** Перекладина в жёлто-чёрную полоску на стойках — пригнуться. */
  function makeBar(width: number): THREE.Group {
    const g = new THREE.Group();
    [-1, 1].forEach((s) => part(g, box, mats.post, s * (width / 2 - 0.15), 1.25, 0, 0.25, 2.5, 0.25));
    const segments = Math.max(4, Math.round(width / 0.8));
    const seg = width / segments;
    for (let i = 0; i < segments; i++) {
      part(g, box, i % 2 ? mats.black : mats.yellow, -width / 2 + seg * (i + 0.5), 2.0, 0, seg, 0.55, 0.35);
    }
    blobShadow(g, width * 0.5, 0.4);
    return g;
  }

  /** Штабель ящиков — разбить ударом. Части разлетаются при `destroyed`. */
  function makeCrates(width: number): THREE.Group {
    const g = new THREE.Group();
    const cols = Math.max(2, Math.round(width / 1.15));
    const size = Math.min(1.35, width / cols);
    for (let c = 0; c < cols; c++) {
      for (let r = 0; r < 2; r++) {
        const crate = new THREE.Group();
        part(crate, box, mats.wood, 0, 0, 0, size, size, size);
        part(crate, box, mats.woodDark, 0, 0, 0, size * 1.02, size * 0.16, size * 1.02);
        part(crate, box, mats.woodDark, 0, 0, 0, size * 0.16, size * 1.02, size * 1.02);
        crate.position.set(-width / 2 + size * (c + 0.5) + (r ? size * 0.25 : 0), size * (r + 0.5), 0);
        crate.userData.home = crate.position.clone();
        crate.userData.velocity = new THREE.Vector3((c - (cols - 1) / 2) * 2.5, 3 + r * 2, -6 - Math.random() * 4);
        crate.userData.spin = new THREE.Vector3(Math.random() * 6, Math.random() * 6, 0);
        g.add(crate);
      }
    }
    g.userData.crates = true;
    blobShadow(g, width * 0.55, 0.8);
    return g;
  }

  /** Гидрант и конусы в полосе — обойти в соседнюю. */
  function makeHydrant(width: number): THREE.Group {
    const g = new THREE.Group();
    const count = Math.max(1, Math.round(width / 5));
    for (let i = 0; i < count; i++) {
      const x = -width / 2 + (width / count) * (i + 0.5);
      part(g, new THREE.CylinderGeometry(0.55, 0.65, 1.7, 12), mats.red, x, 0.85, 0);
      part(g, new THREE.SphereGeometry(0.58, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2), mats.red, x, 1.7, 0);
      part(g, new THREE.CylinderGeometry(0.2, 0.2, 1.7, 8), mats.red, x, 1.1, 0).rotation.z = Math.PI / 2;
      [-1.6, 1.6].forEach((dx) => {
        part(g, new THREE.ConeGeometry(0.32, 1.0, 12), mats.orange, x + dx, 0.5, 0.3);
        part(g, new THREE.CylinderGeometry(0.21, 0.26, 0.14, 12), mats.white, x + dx, 0.55, 0.3);
      });
    }
    blobShadow(g, width * 0.45, 0.8);
    return g;
  }

  function makeObstacle(o: Runner3DObstacle): THREE.Group {
    const width = o.fullWidth ? 14.6 : 4.4;
    if (dataCenter) return dataCenter.makeObstacle(o.type, width, blobShadow);
    switch (o.type) {
      case 'JUMP': return makeWall(width);
      case 'DUCK': return makeBar(width);
      case 'PUNCH': return makeCrates(width);
      default: return makeHydrant(width);
    }
  }

  // ─── Состояние сцены ────────────────────────────────────────────────────
  let tileProto: THREE.Object3D | null = null;
  let backdrop: THREE.Object3D | null = null;
  const tiles: THREE.Object3D[] = [];
  const player = new THREE.Group();
  scene.add(player);
  blobShadow(player, 0.6, 0.45);
  let character: THREE.Object3D | null = null;
  let mixer: THREE.AnimationMixer | null = null;
  /** Клипы по именам `CLIP`; бег крутится сам, остальные ведём вручную. */
  const clipActions = new Map<string, THREE.AnimationAction>();
  const characterMaterials: THREE.MeshStandardMaterial[] = [];

  const obstacleViews = new Map<number, { group: THREE.Group; destroyedAt: number }>();
  let state: Runner3DState = {
    running: false,
    player: { lane: 1, action: 'WALK', progress: 0, lives: 3 },
    obstacles: [],
  };
  let running = false;
  let camZ = 0;
  /** Пройдено с начала забега, м: от него считаются пасхалки ЦОД. */
  let distance = 0;
  let playerX = 0;
  let lastLives = 3;
  let hurtUntil = 0;
  let ready = false;
  let destroyed = false;
  let rafId = 0;
  const clock = new THREE.Clock();

  function resize(): void {
    const w = Math.max(1, options.host.clientWidth || window.innerWidth);
    const h = Math.max(1, options.host.clientHeight || window.innerHeight);
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    // На узком экране отодвигаемся, чтобы три полосы влезали по ширине.
    camera.fov = w / h < 1.4 ? 68 : 55;
    camera.updateProjectionMatrix();
  }

  function syncObstacles(now: number): void {
    const seen = new Set<number>();
    for (const o of state.obstacles) {
      seen.add(o.id);
      let view = obstacleViews.get(o.id);
      if (!view) {
        view = { group: makeObstacle(o), destroyedAt: 0 };
        scene.add(view.group);
        obstacleViews.set(o.id, view);
      }
      const g = view.group;
      g.position.set(o.fullWidth ? 0 : laneX(o.lane), 0, camZ - (PLAYER_UNITS - o.z) * UNIT);
      // Прошедшее игрока не рисуем: оно едет к камере и закрывало бы персонажа
      // в момент удара или прыжка. Разлетающиеся ящики досматриваем.
      // 60 ед = 4,5 м: игрок успевает перелететь или проползти объект целиком,
      // а камера сверху-сзади ещё смотрит поверх него на персонажа.
      g.visible = o.z < PLAYER_UNITS + 60 || Boolean(o.destroyed && g.userData.crates);
      if (o.destroyed && !view.destroyedAt) view.destroyedAt = now;
      if (view.destroyedAt && g.userData.crates) {
        const t = Math.min(1.2, (now - view.destroyedAt) / 1000);
        g.children.forEach((c) => {
          if (!c.userData.home) { c.visible = false; return; }
          c.visible = true;
          const v = c.userData.velocity as THREE.Vector3;
          const home = c.userData.home as THREE.Vector3;
          c.position.set(home.x + v.x * t, Math.max(0.3, home.y + v.y * t - 4.9 * t * t), home.z + v.z * t);
          const spin = c.userData.spin as THREE.Vector3;
          c.rotation.set(spin.x * t, spin.y * t, 0);
        });
      } else if (view.destroyedAt) {
        g.visible = false;
      }
    }
    for (const [id, view] of obstacleViews) {
      if (seen.has(id)) continue;
      scene.remove(view.group);
      obstacleViews.delete(id);
    }
  }

  /** Ставит клип `name` в момент `t` (0…1) с весом `weight`, остальные ручные гасит. */
  function holdClip(name: string | null, t: number, weight: number): void {
    for (const [clip, a] of clipActions) {
      if (clip === CLIP.run) continue;
      const on = clip === name;
      a.setEffectiveWeight(on ? weight : 0);
      if (on) a.time = Math.min(1, Math.max(0, t)) * a.getClip().duration;
    }
    clipActions.get(CLIP.run)?.setEffectiveWeight(1 - (name ? weight : 0));
  }

  function poseCharacter(dt: number, now: number): void {
    const { action, progress, lives } = state.player;
    if (lives < lastLives) hurtUntil = now + 900;
    lastLives = lives;

    // Смена полосы — плавный переход с креном в сторону движения.
    const targetX = laneX(state.player.lane);
    const dx = targetX - playerX;
    playerX += dx * Math.min(1, dt * 9);
    player.position.set(playerX, 0, camZ);

    // Вход в клип и выход из него — короткий бленд с бегом.
    const blend = smoothstep(Math.min(progress, 1 - progress) / CLIP_BLEND);
    let lift = 0;
    if (action === 'JUMP') {
      // Толчок → полёт → приземление; высота держится всю середину, пока объект под ним.
      if (progress < 0.22) holdClip(CLIP.jumpStart, 0.45 + (progress / 0.22) * 0.55, blend);
      else if (progress < 0.78) holdClip(CLIP.jumpLoop, (progress - 0.22) / 0.56, 1);
      else holdClip(CLIP.jumpLand, (progress - 0.78) / 0.22, blend);
      lift = smoothstep(Math.min(progress - 0.12, 0.9 - progress) * 4) * 1.3;
    } else if (action === 'DUCK') {
      // Перекат: самая низкая фаза клипа приходится на середину действия.
      holdClip(CLIP.roll, (progress - 0.12) / 0.76, blend);
    } else if (action === 'PUNCH') {
      holdClip(CLIP.punch, progress, blend);
    } else {
      holdClip(null, 0, 0);
    }
    if (character) {
      character.position.y = lift;
      character.rotation.z = THREE.MathUtils.clamp(-dx * 0.08, -0.35, 0.35);
    }
    mixer?.update(running ? dt : 0);
    const hurt = now < hurtUntil && Math.floor(now / 110) % 2 === 0;
    characterMaterials.forEach((m) => m.emissive.setHex(hurt ? 0x661111 : 0x000000));
  }

  function layout(): void {
    for (let i = 0; i < tiles.length; i++) tiles[i].position.z = TILE_LENGTH - i * TILE_LENGTH;
    if (backdrop) backdrop.position.z = camZ + CAMERA_BACK;
    camera.position.set(playerX * 0.35, CAMERA_UP, camZ + CAMERA_BACK);
    camera.lookAt(playerX * 0.5, 0, camZ - LOOK_AHEAD);
  }

  function frame(): void {
    rafId = 0;
    if (destroyed) return;
    const dt = Math.min(0.1, clock.getDelta());
    const now = performance.now();
    if (running) {
      camZ -= STREET_SPEED * dt;
      distance += STREET_SPEED * dt;
      // Тайлы одинаковые: держим camZ в (−60, 0], препятствия считаются от него.
      while (camZ <= -TILE_LENGTH) camZ += TILE_LENGTH;
    }
    if (ready) {
      dataCenter?.update(distance, camZ, now);
      poseCharacter(dt, now);
      syncObstacles(now);
      layout();
      renderer.render(scene, camera);
    }
    rafId = requestAnimationFrame(frame);
  }

  // ─── Загрузка ──────────────────────────────────────────────────────────
  const loader = new GLTFLoader();
  Promise.all([
    dataCenter ? Promise.resolve(null) : loader.loadAsync(`${base}runner_street.glb`),
    loader.loadAsync(`${base}runner.glb`),
  ]).then(([street, runner]) => {
    if (destroyed) return;
    if (dataCenter) {
      tileProto = dataCenter.tile;
      backdrop = dataCenter.backdrop;
    } else {
      tileProto = street?.scene.getObjectByName('Tile') ?? null;
      backdrop = street?.scene.getObjectByName('Backdrop') ?? null;
      if (!tileProto || !backdrop) throw new Error('runner_street.glb: нет Tile / Backdrop');
      street!.scene.remove(tileProto, backdrop);
    }
    scene.add(backdrop);
    for (let i = 0; i < TILES_AHEAD + 2; i++) {
      const t = tileProto.clone();
      scene.add(t);
      tiles.push(t);
    }

    const model = runner.scene;
    // Причёска висит на кости головы: без обновления матриц до первого кадра
    // она считается в начале координат и раздувает рост.
    model.updateMatrixWorld(true);
    const bounds = new THREE.Box3().setFromObject(model);
    const height = bounds.max.y - bounds.min.y || 1;
    const k = PLAYER_HEIGHT / height;
    const holder = new THREE.Group();
    model.scale.setScalar(k);
    model.position.y = -bounds.min.y * k;
    // В файле персонаж смотрит на +Z; бежит от камеры — к −Z.
    model.rotation.y = Math.PI;
    holder.add(model);
    player.add(holder);
    character = holder;
    model.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.isMesh) {
        mesh.frustumCulled = false; // скин двигает вершины за пределы исходного bounding box
        const m = mesh.material as THREE.MeshStandardMaterial;
        if (m && m.emissive) {
          // Матовый материал под наш свет; эмиссив — для мигания при уроне.
          m.emissiveMap = null;
          m.metalness = 0;
          m.roughness = 0.75;
          characterMaterials.push(m);
        }
      }
    });
    mixer = new THREE.AnimationMixer(model);
    for (const clip of runner.animations) {
      const a = mixer.clipAction(clip);
      // Ручные клипы стоят на месте: время выставляет `holdClip`.
      if (clip.name !== CLIP.run) a.timeScale = 0;
      a.setEffectiveWeight(clip.name === CLIP.run ? 1 : 0);
      a.play();
      clipActions.set(clip.name, a);
    }
    if (!clipActions.has(CLIP.run)) throw new Error(`runner.glb: нет клипа ${CLIP.run}`);

    ready = true;
    resize();
    poseCharacter(0, performance.now());
    layout();
    renderer.render(scene, camera);
    // Сразу после первого кадра, не из rAF: скрытый WebView rAF не крутит.
    options.onReady?.();
  }).catch((e: unknown) => fail(e instanceof Error ? e.message : String(e)));

  canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); fail('webgl context lost'); });
  addEventListener('resize', resize);
  resize();
  rafId = requestAnimationFrame(frame);

  return {
    setState(next) {
      state = next;
      running = next.running;
    },
    start() {
      camZ = 0;
      distance = 0;
      dataCenter?.reset();
      running = true;
      clock.getDelta();
    },
    pause() { running = false; },
    resume() { clock.getDelta(); running = true; },
    restart() {
      running = false;
      camZ = 0;
      distance = 0;
      dataCenter?.reset();
      obstacleViews.forEach((v) => scene.remove(v.group));
      obstacleViews.clear();
    },
    destroy() {
      destroyed = true;
      cancelAnimationFrame(rafId);
      removeEventListener('resize', resize);
      renderer.dispose();
      canvas.remove();
    },
  };
}
